import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
  QuantitySelectorUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import {
  appendDebugLineClassifications,
  ensureCartStateSynced,
  getStateClassificationsFromForm,
  hasSelectedState,
  isSampleCartValidationError,
  LINE_CLASSIFICATIONS_PROPERTY,
  SAMPLE_CART_VALIDATION_MESSAGE,
  SELECTED_STATE_STORAGE_KEY,
  wouldSampleCartRejectAdd,
  wouldSampleCartRejectAddSync,
} from '@theme/beaumont-cart-state';
import {
  bindCartOrderConflict,
  getCartOrderConflictContext,
  getCartOrderConflictElement,
  hideCartOrderConflict,
  showCartOrderConflict,
  showCartOrderConflictForContext,
  syncCartOrderConflictButtons,
} from '@theme/cart-order-conflict';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @property {HTMLElement} [addToCartTotalValue] - Live total amount when show_total is enabled.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {AbortController | undefined} */
  #totalAbortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    this.#bindTotalUpdates();
    this.#updateTotalFromQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
    this.#totalAbortController?.abort();
  }

  #bindTotalUpdates() {
    if (this.dataset.showTotal !== 'true') return;

    this.#totalAbortController?.abort();
    this.#totalAbortController = new AbortController();
    const { signal } = this.#totalAbortController;

    const productForm = this.closest('product-form-component');
    productForm?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    productForm?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateForTotal, { signal });
  }

  /**
   * @param {Event} event
   */
  #onQuantityUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    // Cart line quantity selectors include a cartLine index — ignore those.
    if (event.detail?.cartLine != null) return;
    this.#updateTotalFromQuantity(event.detail?.quantity);
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdateForTotal = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    const sourceButton = event.detail?.data?.html?.querySelector?.(
      'add-to-cart-component[data-show-total="true"]',
    );
    if (sourceButton instanceof HTMLElement) {
      const unitPrice = sourceButton.dataset.unitPrice;
      if (unitPrice) this.dataset.unitPrice = unitPrice;
      const media = sourceButton.dataset.productVariantMedia;
      if (media) this.dataset.productVariantMedia = media;
    }
    this.#updateTotalFromQuantity();
  };

  /**
   * @param {number} [quantityOverride]
   */
  #updateTotalFromQuantity(quantityOverride) {
    if (this.dataset.showTotal !== 'true') return;

    const totalEl = this.refs.addToCartTotalValue;
    if (!totalEl) return;

    const unitPrice = Number(this.dataset.unitPrice || 0);
    if (!Number.isFinite(unitPrice)) return;

    let quantity = quantityOverride;
    if (quantity == null) {
      const productForm = this.closest('product-form-component');
      const quantityInput = productForm?.querySelector?.('quantity-selector-component [name="quantity"]');
      quantity = Number(
        quantityInput instanceof HTMLInputElement ? quantityInput.value : productForm?.dataset?.quantityDefault || 1,
      );
    }
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || '';
    totalEl.textContent = formatMoney(unitPrice * quantity, moneyFormat, currency);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * Success animations (tick / fly-to-cart) run after the cart add succeeds.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before proceeding
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't proceed if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const isBuySampleButton = this.refs.addToCartButton.classList.contains('twp-product-card__buy-sample-button');

      if (isBuySampleButton) {
        if (this.refs.addToCartButton.dataset.loading === 'true') {
          event.preventDefault();
          return;
        }

        if (productForm?.hasAttribute('data-requires-selected-state') && !hasSelectedState()) {
          event.preventDefault();
          return;
        }

        this.refs.addToCartButton.dataset.loading = 'true';
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Tick + optional fly-to-cart. Call only after a successful cart add.
   */
  playSuccessfulAddAnimation() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }

    this.animateAddToCart();
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    addToCartButton.removeAttribute('data-loading');
    addToCartButton.disabled = false;

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number | undefined} */
  #variantChangeWatchdog;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #conflictBlocked = false;

  /** @type {HTMLElement | null} */
  #conflictMessage = null;

  connectedCallback() {
    super.connectedCallback();

    // Combined-listing <main> morphs can disconnect/reconnect this element. An aborted
    // controller cannot be reused — listeners bound with its signal never fire, which
    // left #variantChangeInProgress stuck after sibling switches.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Listen on document (not the section node): section remounts during CL morphs drop
    // section-scoped listeners even when this form instance is preserved/moved.
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Remount must not inherit a stuck gate from the pre-morph lifecycle.
    this.#variantChangeInProgress = false;
    if (this.#variantChangeWatchdog) {
      window.clearTimeout(this.#variantChangeWatchdog);
      this.#variantChangeWatchdog = undefined;
    }

    this.#initCartOrderConflict(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#variantChangeWatchdog) {
      window.clearTimeout(this.#variantChangeWatchdog);
      this.#variantChangeWatchdog = undefined;
    }
    this.#variantChangeInProgress = false;
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    const cart = /** @type {Cart | undefined} */ (event.detail?.resource);

    // Skip quantity sync if this event came from this component
    if (event.detail?.sourceId !== this.id && event.detail?.data?.source !== 'product-form-component') {
      if (cart?.items) {
        this.#updateCartQuantityFromData(cart);
      } else {
        await this.#fetchAndUpdateCartQuantity();
      }
    }

    // Prefer the event cart so conflict state updates without waiting on /cart.js.
    await this.#refreshConflictBlocked(cart);
  };

  /**
   * Sample / product mix: clickable ATC; conflict messaging on click when cart would mix types.
   * @param {AbortSignal} signal
   */
  #initCartOrderConflict(signal) {
    const context = getCartOrderConflictContext(this);

    if (!context) {
      this.#syncAtcDebugAttrs();
      return;
    }

    this.#conflictMessage = getCartOrderConflictElement(context);

    bindCartOrderConflict({
      root: this,
      // Resolve lazily — combined-listing morphs replace the banner node.
      getConflictMessage: () => this.#resolveConflictMessage(),
      isBlocked: () => this.#conflictBlocked,
      getAddButtons: () => this.#getAddToCartButtons(),
      revalidateBlocked: () => this.#refreshConflictBlocked(),
      storageKeys: [SELECTED_STATE_STORAGE_KEY],
      signal,
    });

    document.addEventListener(
      'state-selector:change',
      () => {
        this.#refreshConflictBlocked();
      },
      { signal }
    );

    this.#refreshConflictBlocked();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #resolveConflictMessage() {
    const context = getCartOrderConflictContext(this);
    if (!context) {
      this.#conflictMessage = null;
      return null;
    }

    const scope =
      this.closest('product-availability-component') ||
      this.closest('.product-sample') ||
      this;

    this.#conflictMessage = getCartOrderConflictElement(context, scope);
    return this.#conflictMessage;
  }

  #clearVariantChangeGate() {
    this.#variantChangeInProgress = false;
    if (this.#variantChangeWatchdog) {
      window.clearTimeout(this.#variantChangeWatchdog);
      this.#variantChangeWatchdog = undefined;
    }
    this.#syncAtcDebugAttrs();
  }

  /**
   * Mirror ATC gate / sample-mix flags onto data attrs for ?debug=true panel.
   */
  #syncAtcDebugAttrs() {
    const atcStatus = this.#conflictBlocked ? 'blocked-sample' : 'add';
    this.dataset.debugAtc = atcStatus;
    this.dataset.debugVariantGate = this.#variantChangeInProgress ? 'in_progress' : 'idle';
    this.dataset.debugConflictBlocked = this.#conflictBlocked ? 'true' : 'false';

    document.dispatchEvent(
      new CustomEvent('beaumont:atc-debug', {
        detail: {
          productId: this.dataset.productId ?? null,
          context: getCartOrderConflictContext(this),
          atc: atcStatus,
          variantGate: this.dataset.debugVariantGate,
          conflictBlocked: this.#conflictBlocked,
        },
      })
    );
  }

  #flushAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];
    this.#processBatchAddToCart(queuedItems);
  }

  #syncConflictButtons() {
    syncCartOrderConflictButtons(() => this.#getAddToCartButtons());
  }

  /**
   * @param {{ attributes?: Record<string, string>, items?: Array<{ properties?: Record<string, string> }> } | null | undefined} [cart]
   * @returns {Promise<boolean>}
   */
  async #refreshConflictBlocked(cart) {
    if (!getCartOrderConflictContext(this)) return false;

    this.#resolveConflictMessage();

    try {
      if (cart && Array.isArray(cart.items)) {
        this.#conflictBlocked = wouldSampleCartRejectAddSync(this, cart);
      } else {
        this.#conflictBlocked = await wouldSampleCartRejectAdd(this);
      }
    } catch (error) {
      console.error('Failed sample cart conflict check', error);
      this.#conflictBlocked = false;
    }

    this.#syncConflictButtons();
    this.#syncAtcDebugAttrs();

    if (!this.#conflictBlocked && this.#conflictMessage) {
      hideCartOrderConflict(this.#conflictMessage);
    }

    return this.#conflictBlocked;
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getAddToCartButtons() {
    return Array.from(
      this.querySelectorAll(
        [
          'add-to-cart-component [ref="addToCartButton"]',
          'button.tile-calculator__submit[data-submit-type="submit"]',
          'button.product-sample__button[data-submit-type="submit"]',
        ].join(', ')
      )
    ).filter((button) => button instanceof HTMLButtonElement);
  }

  /**
   * @param {boolean} loading
   */
  #setAddToCartLoading(loading) {
    for (const button of this.#getAddToCartButtons()) {
      if (loading) {
        button.dataset.loading = 'true';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        continue;
      }

      button.removeAttribute('data-loading');
      button.removeAttribute('aria-busy');

      if (button.dataset.submitType === 'submit') {
        button.disabled = false;
      }
    }

    if (!loading && getCartOrderConflictContext(this)) {
      this.#syncConflictButtons();
    }
  }

  /**
   * Shows success animations after a successful add to cart.
   */
  #playSuccessfulAddAnimations() {
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      container.playSuccessfulAddAnimation?.();
    }
  }

  /**
   * Resets buy sample button loading state after a failed add to cart.
   */
  #resetBuySampleButtons() {
    this.#setAddToCartLoading(false);
  }

  /**
   * Surfaces cart add errors in the sample/product conflict banner when available.
   *
   * @param {{ message?: string, description?: string }} response
   * @param {HTMLElement | null | undefined} addToCartTextError
   * @returns {boolean}
   */
  #handleCartAddError(response, addToCartTextError) {
    const conflictContext = getCartOrderConflictContext(this);
    const message = response.message || response.description || '';

    if (!message || !conflictContext) {
      return false;
    }

    const isSampleError = isSampleCartValidationError(message);

    showCartOrderConflictForContext(conflictContext, {
      body: message,
      hideTitle: !isSampleError,
    });

    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }

    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);

    return true;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (this.hasAttribute('data-requires-selected-state') && !hasSelectedState()) {
      this.#resetBuySampleButtons();
      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    // Product form helpers inject these for PDP section re-renders — strip them so
    // cart/add only receives cart API fields (+ our line properties).
    formData.delete('product-id');
    formData.delete('section-id');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    try {
      if (await wouldSampleCartRejectAdd(this)) {
        this.#conflictBlocked = true;
        this.#resetBuySampleButtons();
        this.#syncConflictButtons();
        this.#syncAtcDebugAttrs();

        const messageEl = this.#resolveConflictMessage();
        if (messageEl) {
          showCartOrderConflict(messageEl);
        } else {
          const conflictContext = getCartOrderConflictContext(this);
          if (conflictContext) {
            showCartOrderConflictForContext(conflictContext);
          } else {
            this.#handleCartAddError({ message: SAMPLE_CART_VALIDATION_MESSAGE }, addToCartTextError);
          }
        }

        return;
      }
    } catch (error) {
      console.error('Failed sample cart validation before add to cart', error);
    }

    appendDebugLineClassifications(formData, getStateClassificationsFromForm(this));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      await ensureCartStateSynced();
    } catch (error) {
      console.error('Failed to sync selected state before add to cart', error);
    }

    this.#setAddToCartLoading(true);

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.#resetBuySampleButtons();

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (this.#handleCartAddError(response, addToCartTextError)) {
            return;
          }

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.#playSuccessfulAddAnimations();

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        this.#resetBuySampleButtons();
      })
      .finally(() => {
        this.#setAddToCartLoading(false);

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  async #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    try {
      if (await wouldSampleCartRejectAdd(this)) {
        this.#conflictBlocked = true;
        this.#syncConflictButtons();
        this.#syncAtcDebugAttrs();

        const messageEl = this.#resolveConflictMessage();
        if (messageEl) {
          showCartOrderConflict(messageEl);
        } else {
          const conflictContext = getCartOrderConflictContext(this);
          if (conflictContext) {
            showCartOrderConflictForContext(conflictContext);
          } else if (addToCartTextError) {
            this.#handleCartAddError({ message: SAMPLE_CART_VALIDATION_MESSAGE }, addToCartTextError);
          }
        }

        return;
      }
    } catch (error) {
      console.error('Failed sample cart validation before batch add to cart', error);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const classifications = getStateClassificationsFromForm(this);
    /** @type {Record<string, string> | undefined} */
    let lineProperties;
    if (classifications && Object.keys(classifications).length > 0) {
      lineProperties = {
        [LINE_CLASSIFICATIONS_PROPERTY]: JSON.stringify(classifications),
      };
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        ...(lineProperties ? { properties: lineProperties } : {}),
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    try {
      await ensureCartStateSynced();
    } catch (error) {
      console.error('Failed to sync selected state before batch add to cart', error);
    }

    this.#setAddToCartLoading(true);

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (this.#handleCartAddError(response, addToCartTextError)) {
            return;
          }

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        this.#playSuccessfulAddAnimations();

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#setAddToCartLoading(false);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Whether a bubbled variant event belongs to this form's product context.
   * @param {Event} event
   * @returns {boolean}
   */
  #isVariantEventInScope(event) {
    const scope = this.closest('.shopify-section, dialog, product-card') || this;
    const target = event.target;
    return target instanceof Node && scope.contains(target);
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldHandleVariantUpdate(event) {
    if (!(event instanceof VariantUpdateEvent)) return false;

    if (this.#isVariantEventInScope(event)) return true;

    // After CL <main> morph the section node may have been replaced; the live picker in
    // our (new) section already reflects the sibling product — still adopt that update.
    const newProduct = event.detail?.data?.newProduct;
    if (!newProduct?.id) return false;

    const scope = this.closest('.shopify-section, dialog, product-card') || this;
    const picker = scope.querySelector('variant-picker');
    return picker instanceof HTMLElement && picker.dataset.productId === String(newProduct.id);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (!this.#shouldHandleVariantUpdate(event)) return;

    // Unlock ATC before any ref/morph work — a throw or remount must not leave the gate stuck.
    this.#clearVariantChangeGate();

    // Sample forms skip markup morphs, but still need conflict state after CL switches.
    if (this.dataset.ignoreVariantUpdates === 'true') {
      if (event.detail.data.newProduct) {
        await this.#refreshConflictBlocked();
      }
      return;
    }

    // Combined listing product switch: <main> already has the new product's form markup.
    // Sync ids / queue state, then re-evaluate sample mix against the new product.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      const { variantId } = this.refs;
      if (variantId) {
        variantId.value = event.detail.resource?.id ?? '';
      }

      await this.#refreshConflictBlocked();
      this.#flushAddToCartQueue();
      return;
    }

    if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    if (!variantId) {
      return;
    }
    variantId.value = event.detail.resource?.id ?? '';

    this.#flushAddToCartQueue();

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Re-apply conflict button state after morph may have reset type="submit".
    this.#syncConflictButtons();

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {Event} event */
  #onVariantSelected = (event) => {
    if (this.dataset.ignoreVariantUpdates === 'true') return;
    if (!this.#isVariantEventInScope(event)) return;

    this.#variantChangeInProgress = true;
    this.#syncAtcDebugAttrs();

    // Combined-listing <main> morph can replace the picker that would fire variant:update.
    // If that event never arrives, unlock ATC so the form is not stuck forever.
    if (this.#variantChangeWatchdog) {
      window.clearTimeout(this.#variantChangeWatchdog);
    }

    this.#variantChangeWatchdog = window.setTimeout(() => {
      if (!this.#variantChangeInProgress) return;

      this.#clearVariantChangeGate();
      this.#flushAddToCartQueue();
    }, 4000);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
